import Wrap from './Wrap'

/* Vertical rhythm has exactly two tiers, and the contrast between them is what
 * paces a page this long:
 *
 *   statement — one idea, given the air to land. No label rail; type goes wide.
 *   content   — lists, cards, diagrams. Tighter, and labelled.
 *
 * A third tier would flatten the difference back into a metronome.
 */
const PAD = {
  statement: 'py-28 md:py-44',
  content: 'py-20 md:py-32',
}

/* Three grounds, and they run the full width of the viewport — a dark moment is
 * an edge-to-edge band, never a rounded card floating in white. `tint` is a
 * whisper off white, used to group neighbouring sections; it carries no rule,
 * because the tonal step already separates them. Only same-ground neighbours
 * need a hairline. */
const TONE = {
  light: { bg: 'bg-[var(--paper)]', rule: 'border-t border-[var(--line)]', label: 'text-[var(--faint)]' },
  tint: { bg: 'bg-[var(--tint)]', rule: '', label: 'text-[var(--faint)]' },
  dark: { bg: 'bg-[#0A0A0A]', rule: '', label: 'text-[rgba(245,245,243,.5)]' },
}

/* When a `label` is given, it sits in a left margin rail at ≥lg and the content
 * runs in a measure-width column beside it. The rail is not decoration: the
 * label names the section while the column argues it, which is how a spec sheet
 * reads — appropriate for a product whose whole pitch is "specify the workload."
 * Statement sections drop the rail so nothing competes with the line. */
export default function Section({ id, label, tier = 'content', tone = 'light', divider = true, className = '', children }) {
  const t = TONE[tone]
  const rule = divider ? t.rule : ''

  return (
    <section id={id} className={`${t.bg} ${rule} ${PAD[tier]} ${className}`}>
      <Wrap>
        {label ? (
          <div className="lg:grid lg:grid-cols-[160px_minmax(0,1fr)] lg:gap-x-16">
            <div className="mb-9 lg:mb-0 lg:pt-2.5">
              <span className={`font-mono text-[11.5px] tracking-[0.14em] uppercase ${t.label}`}>{label}</span>
            </div>
            <div className="max-w-[680px]">{children}</div>
          </div>
        ) : (
          <div className="max-w-[880px]">{children}</div>
        )}
      </Wrap>
    </section>
  )
}
